import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  Box,
  Checkbox,
  TextField,
  Typography,
  styled
} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React, { useEffect, useState } from "react";
import { AutoCompleteValues } from "types/AutoCompleteValues";
import { PreSelectedAutocompleteProps } from "types/PreSelectedAutocompleteProps";
import { AutoCompleteStyle } from "./AutoCompleteStyle";
import { useTranslation } from "react-i18next";

const StyledFormControl = styled(FormControl)`
  margin: 1;
  width: 100%;
  font-size: "9px",
`;
const AutocompleteWrapper = styled(Box) <{ height?: string }>`
position: relative;
display: inline-block;
width: 100%;
max-height: ${(props) => props.height || 'auto'};
height: ${(props) => props.height || 'auto'};
`;


const PreSelectedAutocomplete = ({
  options,
  label,
  onChange,
  selectedValues,
  required,
  isConcateText = false,
  isLoading = false,
  renderCode = false,
  useZIndex = true,
  height,
  isPlanningPlant = false,
  disabled
}: PreSelectedAutocompleteProps) => {
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const [, setValues] = useState(selectedValues || []);
  const [t,] = useTranslation();
  const [open, setOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  const handleChange = (
    event: React.SyntheticEvent,
    values: AutoCompleteValues[],
    reason: AutocompleteChangeReason
  ) => {
    setValues(values);
    onChange(values);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFilteredOptions(options);
    if (options.length < 1) {
      setHasError(true);
    }
    else {
      setHasError(false);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    let filteredOptions: AutoCompleteValues[];

    if (renderCode) {
      filteredOptions = options.filter((option) => {
        return option.code?.toLowerCase().includes(inputValue.toLowerCase());
      });
    } else {
      filteredOptions = options.filter((option) => {
        return (
          option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
          option.id.toLowerCase().includes(inputValue.toLowerCase())
        );
      });
    }
    setFilteredOptions(filteredOptions);
  };

  const getLabel = (option: AutoCompleteValues) => {
    return (isPlanningPlant ? `${option.planningPlantId}` : `${option.maintenancePlantId}`) + ` - ${option.name}`;
  } 

  const renderOptions = (props: React.HTMLAttributes<HTMLLIElement>, option: AutoCompleteValues, state: AutocompleteRenderOptionState) => {
    return (
      <li {...props} key={`${option.id}`}>
        {(
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            style={{ marginRight: 1 }}
            checked={state.selected}
          />
        )}
        {getLabel(option)}
      </li>
    );
  }

  const renderInput = (params: AutocompleteRenderInputParams) => {
    return (
      <TextField
        {...params}
        size="small"
        label={label}
        variant="outlined"
        error={required && hasError}
        InputLabelProps={{
          style: { fontSize: 12 },
        }}
        onChange={handleInputChange}
      />
    );
  }

  return (
    <AutocompleteWrapper height={height}>
      <StyledFormControl required={required} error={required && hasError}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          size="small"
          limitTags={1}
          disablePortal={true}
          options={filteredOptions}
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          loading={isLoading}
          noOptionsText={t('NoOptionsText')}
          loadingText={t('LoadingText')}
          value={selectedValues}
          getOptionLabel={getLabel}
          onChange={handleChange}
          style={{ width: "100%" }}
          sx={useZIndex ? AutoCompleteStyle : undefined}
          renderOption={renderOptions}
          renderInput={renderInput}
          isOptionEqualToValue={(option: AutoCompleteValues, value: AutoCompleteValues) => option.id === value.id}
          disabled = {disabled}
        />
        {required && hasError && (
          <Typography
            fontSize={10}
            fontWeight={400}
            color="red"
            marginLeft={2}
            variant="subtitle2"
          >
            {label + t("isRequiredText")}
          </Typography>
        )}
      </StyledFormControl>
    </AutocompleteWrapper>
  );
};

export default PreSelectedAutocomplete;
